// =============================================================================
// Front-of-house login (Phase 2) — DRAFT, not yet wired into the app.
//
// "Tap PIN -> ~15-min unlock" against Supabase. The tablet holds a real (anonymous)
// Supabase session; tapping a PIN records a short-lived grant server-side (the
// auth-pin Edge Function) and refreshes the token so it carries the staff claims
// the database checks (app_session / staff_role / site). When the grant lapses
// (~15 min) the next refresh drops the claims and the till re-locks.
//
// Needs (before this runs for real): anonymous sign-ins enabled, the custom-
// access-token hook registered, and auth-pin deployed. The DB half is validated.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { getSupabaseConfig } from "../config/supabase.js";

const cfg = getSupabaseConfig();
const FN_URL = `${cfg.url}/functions/v1/auth-pin`;

// --- the gate for closing the staff-PIN leak ---------------------------------
// Today every tablet downloads the WHOLE staff PIN list from an endpoint that
// asks for no credentials at all, and then decides who you are by matching the
// four digits you tapped against that downloaded list. Anyone who knows the
// (publicly embedded) address can pull every door code for all four shops.
//
// When this gate is on, the login stops matching against the downloaded list and
// asks the server one question instead: "is this one PIN real, and who is it?"
// The list never leaves the server for that purpose.
//
// ON at both trading shops since 1 Oct 2026 (JJ: one PIN list — the Command
// Centre's — so every sign-in can be named; straight to main, no one-tablet soak).
// `?serverpin=off` on a tablet is the instant local way back; taking a host out
// of this list and promoting that shop is the full one.
//
// ⚠️ Deliberately does NOT stop the roster being downloaded, and does NOT scrub
// the cached copy. JJ's Club Card scanner checks the typed PIN against that SAME
// downloaded list, so removing it now would break the scanner mid-shift. Pulling
// the download is the LAST step, once both the till login AND the scanner ask the
// server — see PIN_LEAK_STATUS.md. Until then this shrinks who *decides* the
// login, not what is exposed.
const SERVER_PIN_VERIFY_HOSTS = new Set([
  "trafford.eatrolled.com",
  "meadowhall.eatrolled.com",
]);
export function serverPinVerifyEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  try {
    const q = new URLSearchParams(window.location.search).get("serverpin");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return SERVER_PIN_VERIFY_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// --- one sign-in per page load (Lane CR) -------------------------------------
// PLAIN ENGLISH. Every screen on a till that needs to talk to Supabase calls the
// same helper below to get the tablet a visitor pass. The helper does not notice
// that another screen is already half-way through collecting one, so on a single
// page load three screens ask three times and the tablet ends up with THREE
// visitor passes instead of one.
//
// That matters for two reasons.
//
// 1. There is a budget. Staging allows 30 anonymous sign-ins an hour from one
//    internet address, and a shopping centre puts every tablet in the shop behind
//    ONE address. So "30 an hour" is 30 for the whole centre, and at three passes
//    per page load the real budget is about TEN page loads an hour for the shop.
//    Past that, a screen is refused and the till says "check the wifi" on
//    perfectly good wifi. (Measured on staging in the overnight review of #376,
//    16/17 Sep 2026: three readers on one page load minted three separate
//    accounts, and two agents running proofs from one address exhausted
//    staging's hour. NOBODY HAS READ PRODUCTION'S OWN LIMIT YET — read it in the
//    dashboard's Auth settings before listing a host below.)
// 2. The per-shop badge (Lane CB, migrations 1460/1461) is pinned to ONE of those
//    visitor passes. If the tablet then does its work holding a different one, the
//    badge is simply not there and the shop-scoped feeds let the tablet through —
//    it fails open and nobody sees it happen.
//
// With this gate ON, the first caller collects the pass and everyone who asks
// while that is in flight gets handed the same one. A caller that arrives after a
// pass already exists still gets it with no network call at all, exactly as today.
// A FAILED sign-in is never remembered: the very next caller tries again, so a
// blip cannot leave the page permanently signed out.
//
// The one trade-off worth knowing before a host goes on the list (Review, 17 Sep
// 2026): today a blip hits whichever ONE screen was unlucky, because every screen
// dials in on its own; with this gate on, three screens share one dial-in, so the
// same blip reaches all three at once. That is still the right swap for the case
// this gate exists for — with one sign-in left in the hour, ON gets all three
// screens working where OFF gets one working and two refused — and the callers
// that matter already fall back on their own (to the Sheet, or to their next
// poll) rather than hang. No retry is added here on purpose: retrying straight
// after a REFUSAL would just spend another of the few sign-ins the hour has left
// on a call that is very likely to be refused again.
//
// EMPTY BY DEFAULT — no shop, no behaviour change, nothing to revert. Turning a
// shop on is a reviewed one-line host add; `?anondedup=on|off` overrides for a
// single tablet, the same pattern as `?kioskbind=` in kioskBinding.js and
// `?stockwritedrop=` in stockSync.js.
const ANON_SESSION_DEDUP_HOSTS = new Set([
  // "trafford.eatrolled.com",   // ← one shop at a time, after a soak on one tablet
]);

// The whole gate as a pure function, so the OFF path is testable without a
// browser. `query` is the value of the ?anondedup= parameter (or null).
export function decideAnonDedup({ hostname = "", query = null } = {}) {
  if (query === "on") return true;
  if (query === "off") return false;
  return ANON_SESSION_DEDUP_HOSTS.has(String(hostname || "").toLowerCase());
}

export function anonDedupEnabled() {
  if (typeof window === "undefined") return false; // node/tests: default off
  let q = null;
  try { q = new URLSearchParams(window.location.search).get("anondedup"); } catch (e) {}
  return decideAnonDedup({ hostname: window.location.hostname || "", query: q });
}

// The one sign-in that is currently in flight, per Supabase client. A WeakMap so
// a second client (presence.js has its own) can never join the kiosk client's
// queue, and so nothing is held once a client is gone.
const anonSignInInFlight = new WeakMap();

// The body of ensureAnonSession with its two dependencies handed in, so the unit
// test can drive it with a fake client and no network. Not for app code: every
// caller in the app uses ensureAnonSession() below.
export async function ensureAnonSessionWith(client, dedup = false) {
  const { data } = await client.auth.getSession();
  if (data?.session) return data.session;
  // GATE OFF — byte-for-byte the behaviour that shipped before this lane: ask for
  // a pass, throw if refused, hand it back. No memo is read and none is written.
  if (!dedup) {
    const { data: signed, error } = await client.auth.signInAnonymously();
    if (error) throw error;
    return signed.session;
  }
  // GATE ON — share one in-flight sign-in. Everything from here to the `set` runs
  // without an await, so two callers can never both decide they are first.
  const joined = anonSignInInFlight.get(client);
  if (joined) return joined;
  const mine = (async () => {
    const { data: signed, error } = await client.auth.signInAnonymously();
    if (error) throw error;
    return signed.session;
  })();
  anonSignInInFlight.set(client, mine);
  try {
    return await mine;
  } finally {
    // Cleared whether it worked or not, so a refusal is never cached.
    anonSignInInFlight.delete(client);
  }
}

// Make sure the tablet has a Supabase session (anonymous). Idempotent.
// Exported for the kitchen's realtime doorbell, which needs a signed-in session
// to receive events (0006 read policy) but no PIN.
// Takes no arguments and never has — the gate is read here, not passed in.
export async function ensureAnonSession() {
  return ensureAnonSessionWith(supabase, anonDedupEnabled());
}

// Offline break-glass — a deliberately tiny (2-person: JJ/Adam) allowlist that
// lets an owner unlock a till when the login service is unreachable (dead mall
// wifi). JJ's "server-only + tiny local allowlist" decision, 31 Aug 2026: with
// server-verify on, no network normally means no login; this keeps a break-glass
// so a manager can always get in during an outage.
//
// Stored as SHA-256 of the PIN, never the PIN — not casually readable in source.
// It is still a minimal, accepted exposure (two door codes, brute-forceable from
// the hash) but it replaces the OLD offline fallback that cached the WHOLE
// roster's PINs in plain text on every tablet. Break-glass logins carry
// breakGlass:true so the caller can mark/limit them.
// Both PINs confirmed directly by JJ (31 Aug 2026) — Adam's is his real
// break-glass PIN, NOT his drifted Supabase pin_plain. Each entry is SHA-256 of
// the real 4-digit PIN.
const BREAK_GLASS = new Map([
  ["25f9525ece71bbb5beb98beb01b793a8e900a1cc4fc5891b1948746272da9792", { name: "JJ",   role: "Admin" }],
  ["ce8457d59078a699acb70416f88155a96a906b7b7aad43708402e3a3bcc8a4b4", { name: "Adam", role: "Admin" }],
]);
export async function sha256Hex(s) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(String(s)));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
// The break-glass identity for a PIN, or null. Exported for the unit test.
export async function breakGlassCheck(pin) {
  try {
    const p = String(pin ?? "").trim();
    if (!/^\d{4}$/.test(p)) return null;
    return BREAK_GLASS.get(await sha256Hex(p)) || null;
  } catch (e) { return null; }
}

// How long the till's sign-in waits for the login service before treating it as
// unreachable. A stalled-but-"connected" link (the shops run on mobile data) must
// not leave the PIN pad frozen.
export const LOGIN_TIMEOUT_MS = 8000;

// Tap-PIN login. Verifies the PIN server-side, records the ~15-min grant, then
// refreshes the token so it carries app_session (RLS + realtime now work).
// Returns { ok, staffName, role } or { ok:false, reason?, status } (status = the
// HTTP status the service answered with; `unparsed:true` when the body wasn't
// JSON). When the login service is unreachable (offline), falls back to the
// break-glass allowlist above; otherwise the reply is
// { ok:false, error, unreachable:true, timedOut }.
// Optional `signal` / `timeoutMs` bound the call exactly as they do for verifyPin;
// with neither (every caller except the till's sign-in) the call is as it was and
// still waits for the token refresh. A bounded call returns the "yes" as soon as
// the server gives it and lets the refresh finish in the background.
export async function loginWithPin(pin, { functionKey = null, siteId = null, siteKey = null, signal = null, timeoutMs = 0 } = {}) {
  const bound = boundedSignal(signal, timeoutMs);
  try {
    const session = await bound.race(ensureAnonSession());
    const res = await fetch(FN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
        "apikey": cfg.anonKey,
      },
      body: JSON.stringify({ pin, functionKey, siteId, siteKey }),
      ...(bound.signal ? { signal: bound.signal } : {}),
    });
    const out = await res.json().catch((e) => {
      if (bound.signal && bound.signal.aborted) throw e;   // cut off by the timeout = the network failing
      return { ok: false, unparsed: true };                // not auth-pin's JSON (a proxy / captive-portal page)
    });
    if (!out.ok) return { ...out, status: res.status };   // server reached and said no -> honour it (never break-glass an online refusal)
    if (!bound.signal) {
      await supabase.auth.refreshSession();    // pull a token the access-token hook has stamped
      return out;
    }
    // Bounded (the till's sign-in): the PIN is already verified, so a slow token
    // refresh must not turn that yes into "unreachable". Let it finish on its own.
    supabase.auth.refreshSession().catch(() => {});
    return out;
  } catch (e) {
    // Login service unreachable (offline / dead wifi / timed out). No token, no
    // RLS/realtime, but the break-glass owners can still unlock the till UI to keep trading.
    const bg = await breakGlassCheck(pin);
    if (bg) return { ok: true, staffName: bg.name, role: bg.role, breakGlass: true, allowed: true };
    return { ok: false, error: "Couldn't reach the login service.", unreachable: true, timedOut: bound.timedOut() };
  } finally {
    bound.release();
  }
}

// Did the login service ANSWER and say this PIN is wrong (or ambiguous — two
// active staff share it)? That is auth-pin's 401 with a body of exactly
// { ok:false }. Only that counts. A network failure, a timeout, a server error,
// a bad session, an unreadable body or the gateway's own error JSON is NOT a
// verdict on the PIN — and neither is 429 "locked" (this tablet's anonymous
// session hit the wrong-PIN limit, which says nothing about THIS PIN and must not
// shut the whole till out) — so for all of those the caller may still fall back.
export function serverRefusedPin(reply) {
  if (!reply || typeof reply !== "object" || reply.ok !== false) return false;
  if (reply.status !== 401) return false;
  return Object.keys(reply).every((k) => k === "ok" || k === "status");
}

// Bound a network call with an optional caller AbortSignal and/or a timeout
// (ms), folded into ONE signal for fetch (Lane BT). With neither — the default
// for every existing caller — there is no signal and no timer, so nothing about
// the call changes. `race(p)` lets a step that takes no signal of its own (the
// session bootstrap) be bounded too. Exported for the unit test.
export function boundedSignal(signal = null, timeoutMs = 0) {
  if (!signal && !(timeoutMs > 0)) {
    return { signal: undefined, race: (p) => p, timedOut: () => false, release() {} };
  }
  const ctrl = new AbortController();
  let timedOut = false, timer = null;
  const onAbort = () => { try { ctrl.abort(); } catch (e) {} };
  if (signal) {
    if (signal.aborted) onAbort(); else signal.addEventListener("abort", onAbort, { once: true });
  }
  if (timeoutMs > 0) timer = setTimeout(() => { timedOut = true; onAbort(); }, timeoutMs);
  // Rejects the moment the signal aborts. Pre-handled so it can never surface as
  // an unhandled rejection when nothing happens to be racing it.
  const aborted = new Promise((_, reject) => {
    const fail = () => { const e = new Error("verifyPin aborted"); e.name = "AbortError"; reject(e); };
    if (ctrl.signal.aborted) fail(); else ctrl.signal.addEventListener("abort", fail, { once: true });
  });
  aborted.catch(() => {});
  return {
    signal: ctrl.signal,
    race: (p) => Promise.race([p, aborted]),
    timedOut: () => timedOut,
    release() {
      if (timer) { clearTimeout(timer); timer = null; }
      if (signal) { try { signal.removeEventListener("abort", onAbort); } catch (e) {} }
    },
  };
}

// Verify a PIN WITHOUT logging anyone in — no grant, no token refresh, so it
// never unlocks the till. For the checks that only need "is this PIN valid, and
// whose is it?": the per-action confirm modal, the clock-in/out gate, the admin
// check. Returns { ok, staffName, role, homeSite, initials } or { ok:false }.
// Offline falls back to the same break-glass owners as loginWithPin.
// NOTE: relies on the auth-pin function's verifyOnly branch being DEPLOYED. Until
// that deploy, an old auth-pin would treat this as a login (record a grant). Safe
// because every caller is behind the empty serverPinVerifyEnabled() gate and no
// shop is flipped before the verifyOnly deploy lands.
//
// Lane BT: optional `signal` (a caller's AbortSignal) and `timeoutMs` bound the
// call. Both default to "none", so every existing caller behaves exactly as
// before. When the call is cut short (offline OR timed out) the reply carries
// `unreachable: true` (plus `timedOut: true` for the timeout case) so a caller
// can tell "the network failed" from "the server said no" — the two must show
// different messages. The break-glass fallback applies to a timeout exactly as it
// does to being offline: a slow login service is an unreachable one.
export async function verifyPin(pin, { functionKey = null, siteId = null, signal = null, timeoutMs = 0 } = {}) {
  const bound = boundedSignal(signal, timeoutMs);
  try {
    const session = await bound.race(ensureAnonSession());
    const res = await fetch(FN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
        "apikey": cfg.anonKey,
      },
      body: JSON.stringify({ pin, functionKey, siteId, verifyOnly: true }),
      ...(bound.signal ? { signal: bound.signal } : {}),
    });
    // NB: no refreshSession — never unlocks. A body cut off by the abort is the
    // network failing (handled below), not a malformed reply.
    return await res.json().catch((e) => {
      if (bound.signal && bound.signal.aborted) throw e;
      return { ok: false };
    });
  } catch (e) {
    const bg = await breakGlassCheck(pin);
    if (bg) return { ok: true, staffName: bg.name, role: bg.role, breakGlass: true };
    return { ok: false, error: "Couldn't reach the login service.", unreachable: true, timedOut: bound.timedOut() };
  } finally {
    bound.release();
  }
}

// The current unlocked pass, read straight off the token, or null if locked.
export async function currentPass() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return null;
  try {
    const claims = JSON.parse(atob(token.split(".")[1]));
    if (claims.app_session === "true") {
      return { role: claims.staff_role, site: claims.site, staffId: claims.staff_id };
    }
  } catch (e) { /* not a readable token */ }
  return null;
}

// Lock the till now: refresh so the (cleared/expired) grant drops the claims.
// The grant also auto-expires server-side after ~15 min regardless.
export async function lock() {
  await supabase.auth.refreshSession();
}
